import type { AdminEmiPayment, FinanceRecord, HandLoanRecord } from '../../lib/workspaceStore';
import { getFinanceFacilityMeta } from '../../lib/workspaceStore';
import { inRange, type DateRange } from '../../lib/dateFilter';

export function fmtInr(n: number) {
  return `₹${Math.round(Math.max(0, n)).toLocaleString('en-IN')}`;
}

export function fmtInrCompact(n: number) {
  return fmtInr(n);
}

export function financePrincipal(data: Record<string, unknown>): number {
  return Math.max(0, Number(data.totalAmount || data.amount) || 0);
}

export function handPrincipal(data: Record<string, unknown>): number {
  return Math.max(0, Number(data.loanAmount) || 0);
}

/** Ledger payoff row first, else finance record closedDate field. */
export function closureDateForRecord(
  recordId: string,
  payments: AdminEmiPayment[],
  closedDateField?: string
): string | null {
  const payoff = payments
    .filter((p) => p.recordId === recordId && p.facilityPayoff)
    .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())[0];
  if (payoff?.paidAt) return payoff.paidAt;
  const trimmed = String(closedDateField ?? '').trim();
  return trimmed || null;
}

export function isRecordClosed(recordId: string, payments: AdminEmiPayment[], closedOnRecord?: boolean): boolean {
  if (payments.some((p) => p.recordId === recordId && p.facilityPayoff)) return true;
  return !!closedOnRecord;
}

export type PeriodMetrics = {
  financeCreatedCount: number;
  financeCreatedAmount: number;
  handCreatedCount: number;
  handCreatedAmount: number;
  financeClosedCount: number;
  financeClosedAmount: number;
  handClosedCount: number;
  handClosedAmount: number;
  financeSeizedCount: number;
  financeSeizedAmount: number;
  collectionsCount: number;
  collectionsAmount: number;
  lateFeesAmount: number;
};

export function buildFinanceCreatedRows(
  finances: FinanceRecord[],
  range: DateRange,
  lineFilter: string
) {
  return finances
    .filter((r) => inRange(r.createdAt, range))
    .filter((r) => lineFilter === 'all' || String(r.data.line ?? '').trim() === lineFilter)
    .map((r) => ({
      id: r.id,
      createdAt: r.createdAt,
      customer: String(r.data.customerName ?? '—'),
      regHa: String(r.data.regNo || r.data.haNumber || '—'),
      line: String(r.data.line ?? '—') || '—',
      principal: financePrincipal(r.data as Record<string, unknown>),
    }));
}

export function buildHandCreatedRows(hands: HandLoanRecord[], range: DateRange) {
  return hands
    .filter((r) => inRange(r.createdAt, range))
    .map((r) => ({
      id: r.id,
      createdAt: r.createdAt,
      borrower: String(r.data.name ?? '—'),
      loanType: String(r.data.loanType ?? '—'),
      repayment: String(r.data.repaymentType ?? '—'),
      principal: handPrincipal(r.data as Record<string, unknown>),
    }));
}

export function buildFinanceClosedRows(
  finances: FinanceRecord[],
  payments: AdminEmiPayment[],
  range: DateRange,
  lineFilter: string
) {
  const rows: {
    id: string;
    closedAt: string;
    customer: string;
    regHa: string;
    line: string;
    principal: number;
  }[] = [];
  for (const r of finances) {
    if (lineFilter !== 'all' && String(r.data.line ?? '').trim() !== lineFilter) continue;
    const meta = getFinanceFacilityMeta(r.id);
    if (!isRecordClosed(r.id, payments, meta?.closedOnRecord)) continue;
    const closedAt = closureDateForRecord(r.id, payments, meta?.closedDate);
    if (!closedAt || !inRange(closedAt, range)) continue;
    rows.push({
      id: r.id,
      closedAt,
      customer: String(r.data.customerName ?? '—'),
      regHa: String(r.data.regNo || r.data.haNumber || '—'),
      line: String(r.data.line ?? '—') || '—',
      principal: financePrincipal(r.data as Record<string, unknown>),
    });
  }
  return rows;
}

export function buildHandClosedRows(hands: HandLoanRecord[], payments: AdminEmiPayment[], range: DateRange) {
  const rows: {
    id: string;
    closedAt: string;
    borrower: string;
    loanType: string;
    principal: number;
  }[] = [];
  for (const r of hands) {
    if (!isRecordClosed(r.id, payments)) continue;
    const closedAt = closureDateForRecord(r.id, payments);
    if (!closedAt || !inRange(closedAt, range)) continue;
    rows.push({
      id: r.id,
      closedAt,
      borrower: String(r.data.name ?? '—'),
      loanType: String(r.data.loanType ?? '—'),
      principal: handPrincipal(r.data as Record<string, unknown>),
    });
  }
  return rows;
}

export function buildFinanceSeizedRows(
  finances: FinanceRecord[],
  range: DateRange,
  lineFilter: string
) {
  return finances
    .filter((r) => String(r.data.seized ?? '').toLowerCase() === 'yes')
    .filter((r) => inRange(String(r.data.seizedDate ?? r.updatedAt), range))
    .filter((r) => lineFilter === 'all' || String(r.data.line ?? '').trim() === lineFilter)
    .map((r) => ({
      id: r.id,
      seizedAt: String(r.data.seizedDate ?? '—'),
      customer: String(r.data.customerName ?? '—'),
      regHa: String(r.data.regNo || r.data.haNumber || '—'),
      line: String(r.data.line ?? '—') || '—',
      reason: String(r.data.seizedReason ?? '—'),
      principal: financePrincipal(r.data as Record<string, unknown>),
    }));
}
